import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Brain,
  Sprout,
  Ruler,
  Wheat,
  Droplet,
  Loader,
  BrainCircuit,
  TrendingUp,
  Recycle,
  PieChart
} from 'lucide-react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AILab = () => {
  const [formData, setFormData] = useState({
    crop_area: '',
    crop_type: '',
    harvest_season: 'summer',
    soil_type: '',
    farming_technique: 'traditional',
    temperature: '',
    rainfall: '',
    humidity: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);

  const soilConditions = [
    'Sandy',
    'Clay',
    'Loamy'
  ];

  const cropTypes = [
    "Rice",
    "Wheat",
    "Sugarcane",
    "Corn"
  ];

  const harvestSeasons = [
    "Summer",
    "Winter",
    "Monsoon"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Transform form data to match API requirements
      const apiData = {
        crop_area: parseFloat(formData.crop_area),
        crop_type: formData.crop_type.toLowerCase(),
        harvest_season: formData.harvest_season.toLowerCase(),
        soil_type: formData.soil_type.toLowerCase(),
        farming_technique: formData.farming_technique,
        temperature: parseFloat(formData.temperature),
        rainfall: parseFloat(formData.rainfall),
        humidity: parseFloat(formData.humidity)
      };

      const response = await fetch('https://knowcode-protobuf-ml-lmds.onrender.com/waste_predict', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch predictions');
      }

      const data = await response.json();
      setResult({
        farm_area: data.farm_area,
        predicted_waste: data.predicted_waste
      });

      // Set chart data
      setChartData({
        environmentalFactors: {
          labels: ['Temperature (°C)', 'Rainfall (mm)', 'Humidity (%)', 'Area (acres)'],
          datasets: [{
            label: 'Environmental Factors',
            data: [
              parseFloat(formData.temperature),
              parseFloat(formData.rainfall),
              parseFloat(formData.humidity),
              parseFloat(formData.crop_area)
            ],
            backgroundColor: 'rgba(34, 197, 94, 0.2)',
            borderColor: 'rgba(34, 197, 94, 1)',
            borderWidth: 2,
            fill: true
          }]
        }
      });
    } catch (err) {
      setError('Failed to get predictions. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Chart options
  const radarOptions = {
    scales: {
      r: {
        beginAtZero: true,
        ticks: {
          stepSize: 20
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center p-2 rounded-xl bg-green-100 mb-3">
            <BrainCircuit className="w-6 h-6 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
            AI Agricultural Waste Predictor
          </h1>
          <p className="text-base md:text-lg text-green-700/80 max-w-2xl mx-auto">
            Get accurate predictions for agricultural waste management
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
          {/* Form Card */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-6 lg:sticky lg:top-24 h-fit"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <Ruler className="w-4 h-4 mr-2 text-green-600" />
                    Land Area (in acres)
                  </label>
                  <input
                    type="number"
                    value={formData.crop_area}
                    onChange={(e) => setFormData({...formData, crop_area: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    placeholder="Enter land area"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <Wheat className="w-4 h-4 mr-2 text-green-600" />
                    Crop Type
                  </label>
                  <select
                    value={formData.crop_type}
                    onChange={(e) => setFormData({...formData, crop_type: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    required
                  >
                    <option value="">Select crop type</option>
                    {cropTypes.map((crop) => (
                      <option key={crop} value={crop}>{crop}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <Droplet className="w-4 h-4 mr-2 text-green-600" />
                    Soil Type
                  </label>
                  <select
                    value={formData.soil_type}
                    onChange={(e) => setFormData({...formData, soil_type: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    required
                  >
                    <option value="">Select soil type</option>
                    {soilConditions.map((condition) => (
                      <option key={condition} value={condition}>{condition}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                    Harvest Season
                  </label>
                  <select
                    value={formData.harvest_season}
                    onChange={(e) => setFormData({...formData, harvest_season: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    required
                  >
                    {harvestSeasons.map((season) => (
                      <option key={season} value={season.toLowerCase()}>{season}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                    Average Temperature (°C)
                  </label>
                  <input
                    type="number"
                    value={formData.temperature}
                    onChange={(e) => setFormData({...formData, temperature: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    placeholder="Enter temperature"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <Droplet className="w-4 h-4 mr-2 text-green-600" />
                    Average Rainfall (mm)
                  </label>
                  <input
                    type="number"
                    value={formData.rainfall}
                    onChange={(e) => setFormData({...formData, rainfall: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    placeholder="Enter rainfall"
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <Droplet className="w-4 h-4 mr-2 text-green-600" />
                    Humidity (%)
                  </label>
                  <input
                    type="number"
                    value={formData.humidity}
                    onChange={(e) => setFormData({...formData, humidity: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                    placeholder="Enter humidity"
                    required
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium flex items-center justify-center space-x-2 hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Brain className="w-5 h-5" />
                    <span>Predict Waste</span>
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>

          {/* Results Card */}
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            {result ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-green-600 mb-1">Farm Area</p>
                    <p className="text-2xl font-bold text-green-700">{result.farm_area} acres</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl">
                    <p className="text-sm text-amber-600 mb-1">Predicted Waste</p>
                    <p className="text-2xl font-bold text-amber-700">{result.predicted_waste} tons</p>
                  </div>
                </div>

                {chartData && (
                  <div className="space-y-6">
                    <div className="p-4 bg-white rounded-xl border">
                      <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                        <PieChart className="w-4 h-4 text-green-600" />
                        Environmental Factors Analysis
                      </h3>
                      <div className="h-64">
                        <Radar options={radarOptions} data={chartData.environmentalFactors} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Recycle className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-medium text-blue-800">Waste Management Insights</p>
                  </div>
                  <p className="text-sm text-gray-600">
                    Based on your farm's characteristics, our AI model predicts that your {formData.crop_type.toLowerCase()} 
                    cultivation will generate approximately {result.predicted_waste} tons of agricultural waste. 
                    This prediction can help you plan your waste management strategy effectively.
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-500 p-8">
                <Sprout className="w-12 h-12 text-green-500/50 mb-3" />
                <p className="text-sm">Fill out the form to get waste predictions</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AILab;
